import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import InputWithIcon from "../commons/InputWithIcon";
import CustomSlider from "../commons/CustomSlider";
import { extractErrorMessage, formatCrypto, formatCurrency } from "../../utils";
import SkeletonCard from "../skeletons/SkeletonCard";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import { baseAxios } from "../../apis";
import { IconLoading } from "../../assets/icons";
import { useToast } from "../../context/toastContext";
import useAuthedApi from "../../hooks/useAuthedApi";
import { ArrowPathIcon } from "@heroicons/react/16/solid";

const RadioCircle = ({ checked }) => (
  <div
    className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${
      checked ? "border-gray-800" : "border-gray-300"
    }`}
  >
    {checked && <div className="w-2.5 h-2.5 rounded-full bg-gray-800" />}
  </div>
);

// A dedicated mutation hook for placing a paper trade order.
const usePlacePaperTradeMutation = () => {
  const { authedPost } = useAuthedApi(); // Get the authedPost function
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      // The mutation function is now much simpler
      const response = await authedPost(`/api/paper_trading/order`, payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      addToast(
        "success",
        `Order successfully created for ${variables.symbol}`,
        10000
      );
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["paperTradingSummary"] });
      queryClient.invalidateQueries({ queryKey: ["paperTradingOrders"] });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(
        error?.response?.data?.error || error?.response?.data
      );
      addToast("error", errorMessage, 10000);
    },
  });
};

// ✨ NEW: A dedicated mutation hook for resetting paper trading capital.
const useResetCapitalMutation = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await baseAxios.get(`/api/paper_trading/reset`);
      return response.data;
    },
    onSuccess: (data) => {
      addToast("success", data.message || "Capital successfully reset.", 10000);
      // Invalidate queries to refetch data after reset
      queryClient.invalidateQueries({ queryKey: ["paperTradingSummary"] });
      queryClient.invalidateQueries({ queryKey: ["paperTradingOrders"] });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(
        error?.response?.data?.error || error?.response?.data
      );
      addToast("error", `Reset failed: ${errorMessage}`, 10000);
    },
  });
};

const FormBuySellTrading = ({
  setActiveTab,
  activeTab,
  summary,
  loadingSummary,
  selectedCoin,
}) => {
  const [orderType, setOrderType] = useState("");
  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [total, setTotal] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const [lastEdited, setLastEdited] = useState("slider");
  const [totalError, setTotalError] = useState("");

  const placeOrderMutation = usePlacePaperTradeMutation();
  const resetCapitalMutation = useResetCapitalMutation();
  const handleResetCapital = () => {
    // Optional: Add a confirmation dialog for better UX
    // if (window.confirm("Are you sure you want to reset your capital and clear all trade history?")) {
    //   resetCapitalMutation.mutate();
    // }
    resetCapitalMutation.mutate();
  };

  const getPriceToUse = () => {
    if (orderType === "limit") {
      const priceNum = parseFloat(price);
      return !isNaN(priceNum) && priceNum > 0 ? priceNum : 0;
    }
    if (orderType === "market") {
      return selectedCoin?.current_price || 0;
    }
    return 0;
  };

  const coinBalance =
    summary?.wallets?.find(
      (wallet) => wallet?.coin === selectedCoin?.symbol?.toUpperCase()
    )?.balance || 0;

  // The USDT balance (Portfolio Value)
  const usdtBalance = summary?.current_value || 0;

  useEffect(() => {
    const priceToUse = getPriceToUse();
    if (!orderType || priceToUse <= 0) return;

    // --- SELL LOGIC (Based on Coin Balance) ---
    if (activeTab === "sell") {
      if (lastEdited === "slider") {
        // Slider controls AMOUNT directly (Percentage of Coin Balance)
        // If balance is 0, amount is 0
        const newAmount =
          coinBalance > 0 ? (sliderValue / 100) * coinBalance : 0;
        const newTotal = newAmount * priceToUse;

        setAmount(newAmount > 0 ? newAmount.toFixed(8) : "");
        setTotal(newTotal > 0 ? newTotal.toFixed(6) : "");
      } else if (lastEdited === "amount") {
        const amountNum = parseFloat(amount);
        if (isNaN(amountNum) || amount === "") {
          setTotal("");
          setSliderValue(0);
          return;
        }
        // Calculate Total
        const newTotal = amountNum * priceToUse;
        setTotal(newTotal.toFixed(6));

        // Update Slider based on what % of Coin Balance was typed
        if (coinBalance > 0) {
          const newSliderValue = (amountNum / coinBalance) * 100;
          setSliderValue(Math.min(100, Math.max(0, newSliderValue)));
        }
      } else if (lastEdited === "total") {
        const totalNum = parseFloat(total);
        if (isNaN(totalNum) || total === "") {
          setAmount("");
          setSliderValue(0);
          return;
        }
        // Derive Amount from Total
        const newAmount = totalNum / priceToUse;
        setAmount(newAmount.toFixed(8));

        // Update Slider based on derived Amount vs Coin Balance
        if (coinBalance > 0) {
          const newSliderValue = (newAmount / coinBalance) * 100;
          setSliderValue(Math.min(100, Math.max(0, newSliderValue)));
        }
      }
    }

    // --- BUY LOGIC (Based on USDT/Portfolio Value) ---
    else if (activeTab === "buy") {
      if (lastEdited === "slider") {
        // Slider controls TOTAL USDT (Percentage of Portfolio Value)
        const newTotal =
          usdtBalance > 0 ? (sliderValue / 100) * usdtBalance : 0;
        const newAmount = newTotal / priceToUse;

        setTotal(newTotal > 0 ? newTotal.toFixed(6) : "");
        setAmount(newAmount > 0 ? newAmount.toFixed(8) : "");
      } else if (lastEdited === "amount") {
        const amountNum = parseFloat(amount);
        if (isNaN(amountNum) || amount === "") {
          setTotal("");
          setSliderValue(0);
          return;
        }
        const newTotal = amountNum * priceToUse;
        setTotal(newTotal.toFixed(6));

        // Update Slider based on Total Cost vs USDT Balance
        if (usdtBalance > 0) {
          const newSliderValue = (newTotal / usdtBalance) * 100;
          setSliderValue(Math.min(100, Math.max(0, newSliderValue)));
        }
      } else if (lastEdited === "total") {
        const totalNum = parseFloat(total);
        if (isNaN(totalNum) || total === "") {
          setAmount("");
          setSliderValue(0);
          return;
        }
        const newAmount = totalNum / priceToUse;
        setAmount(newAmount.toFixed(8));

        // Update Slider based on Total Cost vs USDT Balance
        if (usdtBalance > 0) {
          const newSliderValue = (totalNum / usdtBalance) * 100;
          setSliderValue(Math.min(100, Math.max(0, newSliderValue)));
        }
      }
    }
  }, [
    sliderValue,
    amount,
    total,
    price,
    orderType,
    lastEdited,
    selectedCoin,
    coinBalance,
    usdtBalance,
    activeTab,
  ]);

  const resetForm = () => {
    setPrice("");
    setAmount("");
    setTotal("");
    setSliderValue(0);
    setLastEdited("slider");
    setTotalError("");
  };

  const handlePriceChange = (e) => setPrice(e.target.value);
  const handleAmountChange = (e) => {
    let val = e.target.value;
    const numVal = parseFloat(val);

    if (activeTab === "sell") {
      // If user tries to type more than they have, force it to the max balance
      if (numVal > coinBalance) {
        val = coinBalance.toString();
      }
    }

    setAmount(val);
    setLastEdited("amount");
  };
  const handleTotalChange = (e) => {
    if (totalError) setTotalError("");
    setTotal(e.target.value);
    setLastEdited("total");
  };
  const handleSliderChange = (value) => {
    setSliderValue(value);
    setLastEdited("slider");
  };

  useEffect(() => {
    resetForm();
  }, [activeTab, orderType, selectedCoin?.symbol]);

  const handleSubmit = () => {
    setTotalError("");
    const totalNum = parseFloat(total);

    if (totalNum < 5) {
      setTotalError("Total must be at least 5 USDT");
      return;
    }
    if (
      !orderType ||
      !activeTab ||
      !selectedCoin?.symbol ||
      isNaN(totalNum) ||
      totalNum <= 0
    ) {
      console.warn("Missing required fields or total is invalid");
      return;
    }

    const payload = {
      symbol: `${selectedCoin.symbol?.toUpperCase()}USDT`,
      order_type: orderType,
      usdt_qty: totalNum,
      side: activeTab,
      ...(orderType === "limit" && { price: parseFloat(price) }),
    };

    placeOrderMutation.mutate(payload, {
      onSuccess: () => {
        resetForm();
        setOrderType("");
      },
    });
  };

  const mainFormButtonStyle =
    activeTab === "buy"
      ? "bg-green-100 text-green-700 hover:bg-green-200"
      : activeTab === "sell"
      ? "bg-red-100 text-red-700 hover:bg-red-200"
      : "bg-gray-200 text-gray-500 cursor-not-allowed";

  return (
    <div className="w-full">
      <div
        onClick={handleResetCapital}
        className="flex items-center gap-2 justify-end mb-2 text-[#8C1D18] cursor-pointer text-sm"
      >
        {resetCapitalMutation.isPending ? "Resetting..." : "Reset Capital"}
        <ArrowPathIcon
          className={`w-[14px] h-[14px] text-[#8C1D18] ${
            resetCapitalMutation.isPending ? "animate-spin" : ""
          }`}
        />
      </div>
      <div className="rounded-[10px] border border-solid border-[#DDE1E6] bg-[linear-gradient(234deg,rgba(118,111,129,0)_66.84%,#8962FF_254.21%)] p-2 mb-6 relative">
        <div className="grid grid-cols-2 justify-between">
          <div>
            <span className="text-xs text-[#999]">Current Value</span>
            <div className="text-base font-bold text-[#523C72]">
              {loadingSummary ? (
                <SkeletonCard
                  type="text"
                  lines={1}
                  widths={["100px"]}
                  heights={["15px"]}
                />
              ) : (
                `${formatCurrency(summary?.current_value)} USDT`
              )}
            </div>
          </div>
          <div>
            <span className="text-xs text-[#999]">
              Total {selectedCoin?.symbol?.toUpperCase()} Balance
            </span>
            <div className="text-base font-bold text-[#523C72]">
              {loadingSummary ? (
                <SkeletonCard
                  type="text"
                  lines={1}
                  widths={["100px"]}
                  heights={["15px"]}
                />
              ) : (
                `${formatCrypto(
                  coinBalance || 0
                )} ${selectedCoin?.symbol?.toUpperCase()}`
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 justify-between mt-4">
          <div>
            <span className="text-xs text-[#999]">Realized P/L</span>
            <div>
              <span>
                {loadingSummary ? (
                  <SkeletonCard
                    type="text"
                    lines={2}
                    widths={["60px", "40px"]}
                    heights={["15px", "10px"]}
                  />
                ) : (
                  `${formatCurrency(summary?.realized_pnl)} USDT`
                )}
              </span>
              <span
                className={`block text-xs ${
                  summary?.realized_pnl_percent >= 0
                    ? "text-[#1E8610]"
                    : "text-[#FF3B30]"
                } font-normal`}
              >
                {loadingSummary
                  ? null
                  : `${formatCurrency(summary?.realized_pnl_percent)}%`}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs text-[#999]">Unrealized P/L</span>
            <div>
              <span>
                {loadingSummary ? (
                  <SkeletonCard
                    type="text"
                    lines={1}
                    widths={["60px", "40px"]}
                    heights={["15px", "10px"]}
                  />
                ) : (
                  `${formatCurrency(summary?.unrealized_pnl)} USDT`
                )}
              </span>
              <span
                className={`block text-xs ${
                  summary?.unrealized_pnl_percent >= 0
                    ? "text-[#1E8610]"
                    : "text-[#FF3B30]"
                } font-normal`}
              >
                {loadingSummary
                  ? null
                  : `${formatCurrency(summary?.unrealized_pnl_percent)}%`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 p-2 rounded-lg flex items-center gap-2">
        <div
          className={`flex-1 p-2.5 rounded-md text-sm font-semibold text-center cursor-pointer transition-all ${
            activeTab === "buy"
              ? "bg-white text-gray-900 shadow-custom-dual"
              : "text-gray-500 hover:bg-gray-200/70 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("buy")}
        >
          Buy
        </div>
        <div
          className={`flex-1 p-2.5 rounded-md text-sm font-semibold text-center cursor-pointer transition-all ${
            activeTab === "sell"
              ? "bg-white text-gray-900 shadow-custom-dual"
              : "text-gray-500 hover:bg-gray-200/70 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("sell")}
        >
          Sell
        </div>
      </div>

      <div className="mt-6 border px-3 py-1 rounded-lg">
        <p className="text-sm font-normal text-[#697077]">
          Choose your settings
        </p>
        <div className="flex items-center gap-8 mt-3">
          <label
            onClick={() => setOrderType("market")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <RadioCircle checked={orderType === "market"} />
            <span
              className={`text-sm font-medium ${
                orderType === "market" ? "text-gray-900" : "text-gray-500"
              }`}
            >
              Market
            </span>
          </label>
          <label
            onClick={() => setOrderType("limit")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <RadioCircle checked={orderType === "limit"} />
            <span
              className={`text-sm font-medium ${
                orderType === "limit" ? "text-gray-900" : "text-gray-500"
              }`}
            >
              Limit
            </span>
          </label>
        </div>
      </div>

      {!!orderType && (
        <>
          <div className="mt-5 space-y-4">
            <div>
              <label>Price</label>
              <InputWithIcon
                value={price}
                onChange={handlePriceChange}
                placeholder="Type Price"
                rightIcon="USDT"
                disabled={orderType === "market"}
                type="number"
              />
              {orderType === "market" && selectedCoin?.current_price && (
                <p className="text-xs text-gray-500 mt-1">
                  Using market price:{" "}
                  {formatCurrency(selectedCoin.current_price)} USDT
                </p>
              )}
            </div>
            <div>
              <label>Amount</label>
              <InputWithIcon
                value={amount}
                onChange={handleAmountChange}
                placeholder="Type Amount"
                rightIcon={selectedCoin?.symbol?.toUpperCase() ?? "BTC"}
                type="number"
              />
            </div>
          </div>

          {summary?.current_value > 0 && (
            <div className="mt-4">
              <CustomSlider
                percentage={sliderValue}
                setPercentage={handleSliderChange}
              />
            </div>
          )}

          <div className="mt-5">
            <label>Total</label>
            <InputWithIcon
              placeholder="minimum 5 USDT"
              value={total}
              onChange={handleTotalChange}
              rightIcon="USDT"
              type="number"
              className={totalError ? "border-red-500" : ""}
            />
            {totalError && (
              <p className="text-red-500 text-xs mt-1">{totalError}</p>
            )}
          </div>

          <div className="mt-6">
            <button
              disabled={
                !activeTab ||
                !total ||
                parseFloat(total) <= 0 ||
                placeOrderMutation.isPending
              }
              onClick={handleSubmit}
              className={`w-full py-3 rounded-lg text-base font-bold transition-colors ${mainFormButtonStyle}`}
            >
              {placeOrderMutation.isPending ? (
                <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                  <IconLoading className="w-[22px] h-[22px]" />
                </div>
              ) : activeTab === "buy" ? (
                "Buy"
              ) : activeTab === "sell" ? (
                "Sell"
              ) : (
                "Select action"
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FormBuySellTrading;
